/**
 * AreaSubscriptions opens the EPHEMERAL-class firehose subscriptions (incident, spread,
 * weather, help) over a *set of cells* covering an area — the home watch radius, or the
 * currently-viewed map area — so incidents anywhere in that area are surfaced, not
 * only in the single hex under home/center.
 *
 * The caller computes the covering set (e.g. `cellsWithinRadius`), so the subscription
 * matches the tinted radius the user sees. Incidents/spread/weather/help carry res-7..4
 * cell ancestry, so the relay matches an event when any of its ancestors is in the set.
 * Incident, spread and weather are live-tailed and seeded from their read models (current
 * open incidents / estimates / per-cell wind), so they never replay the firehose. Help
 * has no read model yet, so it backfills over a BOUNDED recent window (`lookback`, not
 * `sinceSeq: 0`) so a new zone never replays the whole firehose.
 *
 * FH-5: `trust.label` is NO LONGER here — it moved to a permanent-class trust subscription
 * (full history, no floor), opened once in app.ts. This sub is ephemeral-only, so it is
 * subject to the purge floor: on a reset (spec 3), the seedable layers re-seed from their
 * read models (incident/spread/weather) and help clamps (the relay already streams live
 * from the floor). Each live-tail sub carries an `onReset` that re-runs its read-model seed.
 *
 * Dedupe is by `key`: the caller passes a stable key for an area (e.g.
 * `home:<cell>:<radius>` or `view:<coarse-cell>`); the same key opens its streams once.
 *
 * Lifecycle: each key's four stream handles are retained so they can be closed again.
 * `home:`/`place:` keys are permanent watch areas (opened via `ensure`). The transient
 * `view:` viewport scope uses `ensureExclusive` — a single active key per scope, so
 * entering a new viewport cell closes the cell we left instead of leaking it. The whole
 * `view:` scope is dropped via `closeScope` when the user leaves the map.
 */
import type { ReceivedEvent } from "@beacon/client-core";

/** A firehose subscription handle: an async-iterable of events that can be closed. The
 *  real `Subscription` (client-core) already implements this, so widening the local type
 *  needs no client change — the same trick MessageSubscriptions uses. */
interface Sub extends AsyncIterable<ReceivedEvent> {
  close(): Promise<void>;
}

type SubClient = {
  subscribe(
    filter: { cells: string[]; types: string[]; sinceSeq?: number; lookback?: number },
    ctx?: { onReset?: () => void; onCaughtUp?: (seq: number) => void },
  ): Sub;
};

/** Bounded-backfill window (events) for the weather/label/help layers. They open with
 *  `lookback` instead of `sinceSeq: 0`, so registering a zone makes the relay replay only the
 *  recent tail of the firehose for the zone's cells — not the whole (unbounded) log. Without
 *  this, every new subscription re-scans the entire firehose (a per-user N+1). The deeper fix
 *  is a read-model seed (like incident/spread) so these layers stop backfilling at all. */
const AREA_BACKFILL_LOOKBACK = 50_000;

export interface AreaHandlers {
  onIncident: (evt: ReceivedEvent) => void;
  onSpread: (evt: ReceivedEvent) => void;
  onHelp: (evt: ReceivedEvent) => void;
  onWeather: (evt: ReceivedEvent) => void;
}

export interface AreaSubscriptionsOptions {
  /** Seeds the incident layer from the corroboration read model. */
  seedIncidents?: (cells: string[]) => Promise<Array<{ seq: number; json: Record<string, unknown> }>>;
  /** Seeds the spread layer from the spread (A5) read model, so the spread stream
   *  can live-tail instead of replaying the append-only log from seq 0. */
  seedSpread?: (cells: string[]) => Promise<Array<{ seq: number; json: Record<string, unknown> }>>;
  /** Seeds the weather layer from the spread service's /weather read model, so the weather
   *  stream live-tails instead of backfilling — eliminating its per-zone firehose replay. */
  seedWeather?: (cells: string[]) => Promise<Array<{ seq: number; json: Record<string, unknown> }>>;
  /** Called once when the incident seed succeeds for a zone (even if the result is empty).
   *  Used to flip the zone's loading state from "checking" to "loaded". */
  onIncidentSeeded?: () => void;
  /** Called once when the incident seed has exhausted its retries for a zone (read model
   *  unreachable). Flips the zone's loading state to "stalled" — never to "loaded". */
  onIncidentSeedFailed?: () => void;
  /** Called before each incident seed attempt (attempt n of max) so the UI can show progress. */
  onIncidentSeedAttempt?: (attempt: number, max: number) => void;
  /** Called when the relay signals caughtUp on the HELP stream: the bounded replay for the
   *  zone's cells is complete, so an empty help list is genuinely empty (the help layer's
   *  "loaded" signal; a timer cannot know this). */
  onHelpCaughtUp?: () => void;
}

/** Position of the help stream in a zone's sub list (see ensure()). */
const HELP_SUB_INDEX = 3;

const HELP_TYPES = [
  "app.aid.helpRequest.create", "app.aid.helpRequest.amend", "app.aid.helpRequest.offer",
  "app.aid.helpRequest.accept", "app.aid.helpRequest.transition", "app.aid.helpRequest.note",
];

export class AreaSubscriptions {
  #client: SubClient;
  #handlers: AreaHandlers;
  #seedIncidents?: AreaSubscriptionsOptions["seedIncidents"];
  #onIncidentSeedFailed?: () => void;
  #onIncidentSeedAttempt?: (attempt: number, max: number) => void;
  #onHelpCaughtUp?: () => void;
  #seedSpread?: AreaSubscriptionsOptions["seedSpread"];
  #seedWeather?: AreaSubscriptionsOptions["seedWeather"];
  #onIncidentSeeded?: () => void;
  /** Active key → its open stream handles (incident, spread, weather, label, help). */
  #active = new Map<string, Sub[]>();
  /** key → its cells (for reopening a single layer, e.g. the help stream on manual retry). */
  #cellsOf = new Map<string, string[]>();
  /** Active key → its incident read-model re-seed (for the periodic area-warning refresh). */
  #reseedIncident = new Map<string, () => void>();

  constructor(client: SubClient, handlers: AreaHandlers, opts: AreaSubscriptionsOptions = {}) {
    this.#client = client;
    this.#handlers = handlers;
    this.#seedIncidents = opts.seedIncidents;
    this.#onIncidentSeedFailed = opts.onIncidentSeedFailed;
    this.#onIncidentSeedAttempt = opts.onIncidentSeedAttempt;
    this.#onHelpCaughtUp = opts.onHelpCaughtUp;
    this.#seedSpread = opts.seedSpread;
    this.#seedWeather = opts.seedWeather;
    this.#onIncidentSeeded = opts.onIncidentSeeded;
  }

  /** Open incident/spread/weather/help subscriptions over `cells` (the covering set
   *  for an area). Incident, spread and weather are live-tail + read-model seed (FH-5:
   *  with an `onReset` that re-seeds on a purge-floor reset); help is bounded-backfilled
   *  (`lookback`) and clamps on reset. Deduped by `key`: no-op if the key was already
   *  opened, or if `cells` is empty. Retains the handles for later close. */
  ensure(key: string, cells: string[]): void {
    if (cells.length === 0 || this.#active.has(key)) return;
    // A re-seed for each seedable layer; also used as the live sub's onReset (spec 3 §4).
    const reseedIncident = (): void => this.#seedStream(key, cells, this.#seedIncidents, (e) => this.#handlers.onIncident(e), this.#onIncidentSeeded, this.#onIncidentSeedFailed, this.#onIncidentSeedAttempt);
    this.#reseedIncident.set(key, reseedIncident);
    const reseedSpread = (): void => this.#seedStream(key, cells, this.#seedSpread, (e) => this.#handlers.onSpread(e));
    const reseedWeather = (): void => this.#seedStream(key, cells, this.#seedWeather, (e) => this.#handlers.onWeather(e));
    const subs: Sub[] = [
      // Incident, spread AND weather: LIVE-TAIL (no sinceSeq) — current state is seeded from
      // the read models, so none of them replays the firehose. On an ephemeral purge reset
      // the relay skips backfill ⇒ re-seed current state (seed events carry seq 0, live wins).
      this.#openLive(cells, ["app.aid.incident.snapshot"], (e) => this.#handlers.onIncident(e), reseedIncident),
      this.#openLive(cells, ["app.aid.spread.estimate"], (e) => this.#handlers.onSpread(e), reseedSpread),
      this.#openLive(cells, ["app.aid.weather.conditions"], (e) => this.#handlers.onWeather(e), reseedWeather),
      // Help bounded-backfills a recent window (not the whole firehose) until it gets a
      // read-model seed; on reset it clamps (no onReset — the relay streams live from the floor).
      this.#open(cells, HELP_TYPES, (e) => this.#handlers.onHelp(e)),
    ];
    this.#active.set(key, subs);
    this.#cellsOf.set(key, cells);
    reseedIncident();
    reseedSpread();
    reseedWeather();
  }

  /** Single active key per `scope`: close every active key under `${scope}:` that is not
   *  `key`, then ensure `key`. The viewport uses this so only one `view:` cell streams at
   *  a time — entering a new cell evicts the one we left, bounding the subscription set. */
  ensureExclusive(scope: string, key: string, cells: string[]): void {
    const prefix = `${scope}:`;
    this.#close((k) => k.startsWith(prefix) && k !== key);
    this.ensure(key, cells);
  }

  /** Close + forget every active key under `${scope}:` (e.g. the `view:` scope on leaving
   *  the Now tab — stop streaming the map area the user is no longer looking at). */
  closeScope(scope: string): void {
    const prefix = `${scope}:`;
    this.#close((k) => k.startsWith(prefix));
  }

  /** Re-run the incident read-model seed for every active zone. Used by a periodic timer
   *  (app.ts) so a region-wide warning that began after subscribe — matched server-side by
   *  footprint containment — is pulled in without a relay round-trip. Cheap (O(open incidents)
   *  per zone), bounded by the active key set. */
  reseedIncidents(): void {
    for (const reseed of this.#reseedIncident.values()) reseed();
  }

  /** Close + forget active keys whose key matches. Best-effort: a close() that throws
   *  never escapes (mirrors MessageSubscriptions.#close). */
  #close(match: (key: string) => boolean): void {
    for (const [key, subs] of this.#active) {
      if (!match(key)) continue;
      for (const sub of subs) void Promise.resolve(sub.close()).catch(() => {});
      this.#active.delete(key);
      this.#cellsOf.delete(key);
      this.#reseedIncident.delete(key);
    }
  }

  /** Manual retry for the help layer: close and reopen ONLY the help stream of every active
   *  zone (a fresh bounded replay + a fresh caughtUp). The live-tail layers are untouched. */
  reopenHelp(): void {
    for (const [key, subs] of this.#active) {
      const cells = this.#cellsOf.get(key);
      if (!cells) continue;
      const old = subs[HELP_SUB_INDEX];
      if (old) void Promise.resolve(old.close()).catch(() => {});
      subs[HELP_SUB_INDEX] = this.#open(cells, HELP_TYPES, (e) => this.#handlers.onHelp(e));
    }
  }

  /** Open a BOUNDED-backfill stream (help) and drain it into `on`. Uses `lookback` (last
   *  AREA_BACKFILL_LOOKBACK events) rather than `sinceSeq: 0`, so the relay replays only the
   *  recent tail for these cells instead of the whole firehose per zone. Clamps on reset. */
  #open(cells: string[], types: string[], on: (evt: ReceivedEvent) => void): Sub {
    const onCaughtUp = this.#onHelpCaughtUp;
    const sub = this.#client.subscribe(
      { cells, types, lookback: AREA_BACKFILL_LOOKBACK },
      onCaughtUp ? { onCaughtUp: () => onCaughtUp() } : undefined,
    );
    void this.#pump(sub, on);
    return sub;
  }

  /** Open a LIVE-TAIL stream (no sinceSeq — only events after this call) and drain it.
   *  `onReset` (FH-5) re-seeds this layer when the relay signals a purge-floor reset. */
  #openLive(cells: string[], types: string[], on: (evt: ReceivedEvent) => void, onReset?: () => void): Sub {
    const sub = this.#client.subscribe({ cells, types }, onReset ? { onReset } : undefined);
    void this.#pump(sub, on);
    return sub;
  }

  /** Seed one layer for `key` from a read model. The live tail is already open (its start
   *  seq ≤ this read snapshot ⇒ no gap); seed events carry seq 0 so any live event wins
   *  (the stores fold latest-wins by seq). Dropped if the key was evicted while fetching.
   *  If the fetch throws (read model not ready), retries with bounded backoff (1s/2s/4s/8s);
   *  after exhausting retries, the periodic reseed (app.ts) keeps trying. On first success
   *  `onSuccess` is called once (used to flip the zone's loading state to "loaded");
   *  `onGiveUp` once when the retries are exhausted (loading state → "stalled"). */
  #seedStream(
    key: string,
    cells: string[],
    fetcher: ((cells: string[]) => Promise<Array<{ seq: number; json: Record<string, unknown> }>>) | undefined,
    handle: (evt: ReceivedEvent) => void,
    onSuccess?: () => void,
    onGiveUp?: () => void,
    onAttempt?: (attempt: number, max: number) => void,
  ): void {
    if (!fetcher) return;
    const delays = [1000, 2000, 4000, 8000];
    const attempt = (i: number): void => {
      if (!this.#active.has(key)) return; // evicted while waiting
      onAttempt?.(i + 1, delays.length + 1);
      void (async () => {
        try {
          const events = await fetcher(cells);
          if (!this.#active.has(key)) return;
          for (const e of events) handle(e as unknown as ReceivedEvent);
          onSuccess?.(); // first successful fetch (even empty) = loaded
        } catch {
          if (i < delays.length && this.#active.has(key)) {
            setTimeout(() => attempt(i + 1), delays[i]); // read model not ready → retry
          } else if (this.#active.has(key)) {
            onGiveUp?.(); // give up for now; the periodic reseed (app.ts) keeps trying.
          }
        }
      })();
    };
    attempt(0);
  }

  async #pump(sub: Sub, on: (evt: ReceivedEvent) => void): Promise<void> {
    for await (const evt of sub) on(evt);
  }
}
